import { useMemo } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { GEFAHREN_QUESTIONS } from "./data";
import GefahrenControl from "./GefahrenControl";
import { filterZahlungQuestions } from "./GefahrenSection";
import type {
  GefahrenAnswers,
  GefahrenQuestion,
  KlasseKey,
  TarifierungKey,
} from "./types";

/* ──────────────────────────────────────────────────────────────────────────
   Panel grouping — keeps Vertragsdaten & Zahlung visually in sync with the
   Immobilie contract form. Each panel renders the matching subset of
   questions; if a panel has zero matches we omit it.
   ────────────────────────────────────────────────────────────────────────── */

type PanelKey =
  | "beitrag"
  | "vertragsdaten"
  | "zahlweise"
  | "zahlungsart"
  | "bankverbindung"
  | "rechnungsstellung"
  | "bemerkungen";

const PANEL_LABELS: Record<PanelKey, string> = {
  beitrag: "Beitrag Gesamt jährlich",
  vertragsdaten: "Vertragsdaten",
  zahlweise: "Zahlweise",
  zahlungsart: "Zahlungsart",
  bankverbindung: "Bankverbindung",
  rechnungsstellung: "Rechnungsstellung",
  bemerkungen: "Bemerkungen",
};

const PANEL_ORDER: PanelKey[] = [
  "beitrag",
  "vertragsdaten",
  "zahlweise",
  "zahlungsart",
  "bankverbindung",
  "rechnungsstellung",
  "bemerkungen",
];

const classifyQuestion = (q: GefahrenQuestion): PanelKey => {
  const k = (q.key ?? "").toLowerCase();
  const f = (q.frage ?? "").toLowerCase();

  if (
    k.includes("jahrespraemie") ||
    k.includes("praemie_je_zahlweise") ||
    k.includes("versicherungssteuer") ||
    f.includes("prämie") ||
    f.includes("versicherungssteuer")
  ) {
    return "beitrag";
  }
  if (
    f.includes("versicherungsbeginn") ||
    f.includes("versicherungsablauf") ||
    k.includes("hauptfaelligkeit") ||
    k.includes("naechste_faelligkeit")
  ) {
    return "vertragsdaten";
  }
  if (f.includes("zahlungsart")) return "zahlungsart";
  if (f.includes("zahlweise") || k.includes("zahlweise")) return "zahlweise";
  if (
    f.includes("bankverbindung") ||
    k.includes("iban") ||
    k.includes("bic") ||
    k.includes("kontoinhaber") ||
    k.includes("bank")
  ) {
    return "bankverbindung";
  }
  if (
    f.includes("rechnungsempfänger") ||
    f.includes("rechnungsstellung") ||
    k.includes("rechnung") ||
    k.includes("abweichender_rechnungsempfaenger")
  ) {
    return "rechnungsstellung";
  }
  if (f.includes("bemerk")) return "bemerkungen";
  return "vertragsdaten";
};

/* ──────────────────────────────────────────────────────────────────────────
   Styles
   ────────────────────────────────────────────────────────────────────────── */

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  outerCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    marginBottom: "4px",
  },
  icon: {
    width: "44px",
    height: "44px",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    color: BRAND.ink,
    margin: 0,
  },
  subtitle: {
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  /* Sub-panel matches Immo's subPanelStyle / subTitleStyle */
  subPanel: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    paddingTop: "16px",
    paddingBottom: "16px",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  subPanelFirst: {
    ...shorthands.borderTop("0"),
    paddingTop: "8px",
  },
  subTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
    margin: 0,
  },
  rows: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr",
    columnGap: "16px",
    rowGap: "6px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "minmax(220px, 0.9fr) 1.1fr",
      alignItems: "center",
    },
  },
  label: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  frage: {
    fontSize: "0.95rem",
    color: BRAND.ink,
    fontWeight: 600,
  },
  merkmal: {
    fontSize: "0.78rem",
    color: BRAND.muted,
    fontStyle: "italic",
    whiteSpace: "pre-line",
  },
  notice: {
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontStyle: "italic",
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
  },
});

type Props = {
  tarifierung: TarifierungKey | "";
  klasse: KlasseKey | null;
  answers: GefahrenAnswers;
  onChange: (key: string, value: string) => void;
};

const ZahlungSection = ({ tarifierung, klasse, answers, onChange }: Props) => {
  const styles = useStyles();

  const grouped = useMemo(() => {
    const list =
      tarifierung && klasse ? (GEFAHREN_QUESTIONS[tarifierung]?.[klasse] ?? []) : [];
    const all = filterZahlungQuestions(list);
    const map = new Map<PanelKey, GefahrenQuestion[]>();
    for (const key of PANEL_ORDER) map.set(key, []);
    for (const q of all) {
      const panel = classifyQuestion(q);
      map.get(panel)!.push(q);
    }
    return map;
  }, [tarifierung, klasse]);

  const totalQuestions = Array.from(grouped.values()).reduce(
    (sum, list) => sum + list.length,
    0,
  );

  if (totalQuestions === 0) {
    return (
      <div className={styles.notice}>
        Keine Zahlungs-Felder verfügbar — bitte zuerst Tarifierungsart wählen.
      </div>
    );
  }

  const renderRow = (q: GefahrenQuestion) => (
    <div key={q.key} className={styles.row}>
      <div className={styles.label}>
        <span className={styles.frage}>{q.frage}</span>
        {q.merkmal && <span className={styles.merkmal}>{q.merkmal}</span>}
      </div>
      <div>
        <GefahrenControl
          question={q}
          value={answers[q.key!] ?? ""}
          onChange={onChange}
        />
      </div>
    </div>
  );

  const panelsToRender = PANEL_ORDER.filter(
    (key) => (grouped.get(key) ?? []).length > 0,
  );

  return (
    <div className={styles.root}>
      <div className={styles.outerCard}>
        <div className={styles.header}>
          <span className={styles.icon}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="6" width="16" height="12" rx="2" />
              <path d="M3 10h16" />
              <path d="M7 14h3" />
            </svg>
          </span>
          <div>
            <h3 className={styles.cardTitle}>Vertragsdaten und Zahlung</h3>
            <span className={styles.subtitle}>
              Jahresprämie und Beitragsinformationen oben, anschließend
              Vertragsdaten, Zahlweise, Zahlungsart, Bankverbindung und
              Bemerkungen.
            </span>
          </div>
        </div>

        {panelsToRender.map((key, idx) => (
          <div
            key={key}
            className={mergeClasses(styles.subPanel, (idx === 0) && styles.subPanelFirst)}
          >
            <h4 className={styles.subTitle}>{PANEL_LABELS[key]}</h4>
            <div className={styles.rows}>{grouped.get(key)!.map(renderRow)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ZahlungSection;
